/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useRef, Suspense, useMemo, type ReactNode, Fragment } from 'react';
import { type AnalyticsWebClient } from '@atlassian/forge-ui/analytics';
import {
	ForgeErrorBoundary,
	type Extension,
	ForgeUIExtensionAnalyticsContext,
} from '@atlassian/forge-ui/ui';
import type { DocNode } from '@atlaskit/adf-schema';
import { isCustomUI } from '@atlassian/forge-ui';
import { UFOThirdPartySegment } from '@atlaskit/react-ufo/segment';

import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { ForgeUIExtensionPointProvider } from '@atlassian/forge-ui/provider';
import type { ForgeDoc, ProductEnvironment } from '@atlassian/forge-ui-types';
import { type ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import type ApolloClient from 'apollo-client';
import { token } from '@atlaskit/tokens';
import { dequal } from 'dequal/lite';
import { useIntl } from 'react-intl';
import { fg } from '@atlaskit/platform-feature-flags';

import { getConfig } from '../config';
import { ForgeUIExtension, AdfExportRenderer } from './renderers';

import { isAdfExportEntrypoint } from '../utils/pdfEntrypointUtils';
import type { FlagFunctions } from '../utils/getFlagProvider';
import { type CustomEditContext, type ForgeExtension, type EditorAPI } from '../types';
import {
	calculateViewportSizeHeight,
	type ViewportSizeType,
} from './renderers/calculateViewportSizeHeight';
import { FORGE_DOC_UPDATED_EVENT } from '../utils/constants';

const styles1 = css({
	position: 'relative',
});

const styles2 = css({
	background: 'transparent',
	cursor: 'not-allowed',
	left: 0,
	position: 'absolute',
	height: '100%',
	top: 0,
	width: '100%',
	zIndex: 1,
});

interface ReservedHeightWrapperProps {
	children: ReactNode;
	extension: Extension;
}

const ReservedHeightWrapper = ({ extension, children }: ReservedHeightWrapperProps) => {
	const extensionViewportSize = extension?.properties?.viewportSize as ViewportSizeType | undefined;

	// Only apply reserved height if the feature flag is on AND actualViewportSize is defined
	if (extensionViewportSize && fg('confluence_forge_early_render_reserve_height')) {
		const viewportSizeHeight = calculateViewportSizeHeight(extensionViewportSize);

		// Custom UI extensions have full support for viewportSize, leading to a strict height.
		// UI Kit 2 extensions, while they can define viewportSize in their manifest, primarily
		// have dynamic sizing. This means viewportSize is not a strict constraint for them as it
		// is for Custom UI. Using minHeight based on a manifest-defined viewportSize is therefore
		// a best-effort approach to proactively reserve space. This aims to reduce layout shift
		// while still allowing the extension to grow dynamically if its content requires more space.
		if (isCustomUI(extension)) {
			return (
				<div style={{ height: viewportSizeHeight }} data-testid="reserved-height-wrapper-custom-ui">
					{children}
				</div>
			);
		} else {
			return (
				<div style={{ minHeight: viewportSizeHeight }} data-testid="reserved-height-wrapper-uikit">
					{children}
				</div>
			);
		}
	}

	// If FF is off, or if actualViewportSize is undefined, render children directly
	return <Fragment>{children}</Fragment>;
};

export interface ExtensionRef {
	openConfig: () => Promise<{ [key: string]: any } | void>;
}

const APP_CONTAINER_PADDING = token('space.100');

export interface ForgeUIExtensionWrapperProps {
	accountId: string;
	analyticsWebClient: AnalyticsWebClient | Promise<AnalyticsWebClient>;
	apolloClient?: ApolloClient<object>;
	cloudId: string;
	contextIds: string[];
	customEditContext?: CustomEditContext;
	dataProviders: ProviderFactory;
	editorAPI?: EditorAPI;
	environment: ProductEnvironment;
	extension: ForgeExtension;
	extensionData: Record<string, any>;
	flags: FlagFunctions;
	getContextToken?: () => Promise<string>;
	hideGlassPane?: boolean;
	isEditing: boolean;
	page: string;
	product: string;
	productExtensionHandlers?: ExtensionHandlers;
	useRouteActions?: () => {
		[key: string]: (...args: any[]) => any;
		push: (url: string, forceReload?: boolean) => void;
	};
}

const useMemoedExtensionData = (extensionData: Record<string, any>): Record<string, any> => {
	const ref = useRef(extensionData);
	if (!dequal(ref.current, extensionData)) {
		ref.current = extensionData;
	}
	return ref.current;
};

const ForgeUIExtensionWrapper = ({
	apolloClient,
	contextIds,
	cloudId,
	environment,
	extensionData,
	editorAPI,
	accountId,
	dataProviders,
	extension,
	isEditing,
	flags,
	getContextToken,
	productExtensionHandlers,
	hideGlassPane,
	customEditContext,
	useRouteActions,
}: ForgeUIExtensionWrapperProps) => {
	const elRef = useRef<HTMLDivElement>(null);
	const timerRef = useRef<NodeJS.Timeout | null>(null);

	const {
		extension: gqlExtension,
		extensionId,
		extensionTitle,
		measuredHeight,
		viewportHeight,
		// If `parameters` is missing, we'll let that bubble up to the extension's error boundary.
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	} = extension.parameters!;

	let localId = extension.parameters?.localId || '';
	/**
	 * Macros created by auto convert flow does not generate a `localId`. `localId` is being created by `quickInsert` in convertToExtensionManifest.tsx and is stored in the extension object
	 * Extract `localId` here to be passed into `coreData` below.
	 *
	 */
	if (!localId) {
		localId = extension.localId as string;
	}

	const viewportDimensions = useMemo(
		() => (fg('platform_measure_macro_height') ? { measuredHeight, viewportHeight } : undefined),
		[measuredHeight, viewportHeight],
	);

	const config = getConfig(extension.parameters!);

	const isBodiedMacro = extension.type === 'bodiedExtension';
	const extensionViewData = useMemo(
		() =>
			isBodiedMacro && !!extension.content
				? {
						macro: {
							body: {
								type: 'doc' as const,
								version: 1 as const,
								content: extension.content as DocNode['content'],
							},
						},
					}
				: {},
		[extension.content, isBodiedMacro],
	);

	const isAdfExport = isAdfExportEntrypoint(gqlExtension as Extension);

	const isHostedResource = Boolean(gqlExtension?.properties?.resource);
	const isUiKitTwo = gqlExtension?.properties?.render === 'native';
	const isCustomUI = isHostedResource && !isUiKitTwo;
	// inline macro doesn't support Custom UI
	const isInline = gqlExtension?.properties?.layout === 'inline' && !isCustomUI;

	const coreData = useMemo(() => ({ localId, cloudId }), [localId, cloudId]);
	const macroExtensionData = {
		...(customEditContext
			? {
					isConfiguring: true,
					isInserting: customEditContext.isInserting,
				}
			: {}),
		...(isBodiedMacro ? { layout: 'bodied' } : {}),
	};

	// moves specific macro extension data (isEditing, macro) to extensionData instead of extensionPayload as this is required for Tempo (#hot-300905)
	// the first check enables the feature for Tempo specifically, the second check enables the feature for all extensions if needed
	const enabledForTempo =
		gqlExtension?.appId === '76206a70-b9d6-4328-9e20-db79196c548a' &&
		fg('forge-ui-move-macro-data-for-tempo');
	const shouldIncludeMacroDataInExtensionData =
		enabledForTempo || fg('forge-ui-move-macro-data-to-extension-data');

	// additional data is already added in xen-editor-provider/src/render/renderExtension.tsx (remove comment after flag cleanup)
	const memoedExtensionData = useMemoedExtensionData(extensionData);

	const extensionPayload = useMemoedExtensionData(
		shouldIncludeMacroDataInExtensionData
			? { config }
			: {
					config,
					isEditing,
					...(Object.keys(macroExtensionData).length ? { macro: macroExtensionData } : {}),
				},
	);

	const adfExportExtensionPayload = useMemo(
		() => ({ ...extensionPayload, ...extensionViewData }),
		[extensionPayload, extensionViewData],
	);

	const { locale } = useIntl();

	const handleOnMacroUpdated = (forgeDoc?: ForgeDoc) => {
		if (isBodiedMacro && forgeDoc?.children?.[0].type === 'AdfRenderer') {
			const event = new CustomEvent(FORGE_DOC_UPDATED_EVENT, {
				detail: {
					forgeDoc,
					localId,
				},
				bubbles: true,
			});
			document.dispatchEvent(event);
		}

		if (fg('platform_measure_macro_height')) {
			const isContextPanelOpen =
				editorAPI?.extension?.sharedState?.currentState()?.showContextPanel;

			const actionsAPI = editorAPI?.extension?.actions?.api();

			// Check if the context panel is open to filter for macro config updates
			if (!actionsAPI || !isContextPanelOpen) {
				return;
			}

			// Only call this function in edit/live page mode
			if (elRef.current && isEditing) {
				if (timerRef.current) {
					clearTimeout(timerRef.current);
				}

				timerRef.current = setTimeout(() => {
					const rectHeight = elRef?.current?.getBoundingClientRect().height;
					// allow three seconds for any async effects
					try {
						/**
						 * This attribute will be Forge's effort to estimate and reserve rendered height
						 * to achieve 0 CLS on behalf of vendors. Alongside this change will be some
						 * heuristic measurements on view page to check the accuracy of this over time.
						 */
						actionsAPI.doc.update(localId, (currentNode) => {
							const newNode = {
								attrs: {
									...currentNode.attrs,
									parameters: {
										...currentNode.attrs?.parameters,
										measuredHeight: rectHeight,
									},
								},
							};

							return newNode;
						});
					} catch {}
				}, 3000);
			}
		}
	};

	const renderRenderer = () => {
		if (isAdfExport && isHostedResource) {
			/**
			 * For frontend macro rendering, we only pass the `extensionViewData` (macro body) to the iframe over the JS bridge.
			 * This is to avoid it being included in the Forge Invocation Token (FIT) for remote endpoint resolver calls.
			 * However, the `AdfExport` is a backend function, so we cannot pass it over the JS bridge.
			 * Nevertheless, it won't be included in the FIT, because the export does not use a remote endpoint resolver.
			 * It also won't be included in the Forge Compute Token (FCT), because the FCT only includes context--not `extensionPayload`.
			 */

			// Fallback renderer used when adfExport returns null/undefined, allowing vendors to opt out
			// of ADF rendering for a specific export type (e.g. PDF) and fall back to iframe rendering.
			// This is a render prop (function) so ForgeUIExtension is only mounted — and only fires its
			// invokeExtension call — when AdfExportRenderer has confirmed that adfExport did not return
			// a valid ADF document.
			const fallbackRenderer = () => (
				<ReservedHeightWrapper extension={gqlExtension as Extension}>
					<ForgeUIExtension
						accountId={accountId}
						apolloClient={apolloClient}
						contextIds={contextIds}
						environment={environment}
						extension={gqlExtension as Extension}
						coreData={coreData}
						extensionData={memoedExtensionData}
						extensionPayload={adfExportExtensionPayload}
						extensionViewData={extensionViewData}
						flags={flags}
						getContextToken={getContextToken}
						isEditing={false}
						locale={locale}
						onForgeDocUpdated={handleOnMacroUpdated}
						useRouteActions={useRouteActions}
						viewportDimensions={viewportDimensions}
						productExtensionHandlers={productExtensionHandlers}
					/>
				</ReservedHeightWrapper>
			);

			return (
				<AtlassianRelayEnvironmentProvider>
					<AdfExportRenderer
						accountId={accountId}
						contextIds={contextIds}
						coreData={coreData}
						dataProviders={dataProviders}
						extension={gqlExtension as Extension}
						extensionData={memoedExtensionData}
						extensionPayload={adfExportExtensionPayload}
						extensionId={extensionId}
						extensionTitle={extensionTitle}
						fallbackRenderer={fallbackRenderer}
						getContextToken={getContextToken}
					/>
				</AtlassianRelayEnvironmentProvider>
			);
		}

		return (
			<ReservedHeightWrapper extension={gqlExtension as Extension}>
				<ForgeUIExtension
					accountId={accountId}
					apolloClient={apolloClient}
					contextIds={contextIds}
					environment={environment}
					extension={gqlExtension as Extension}
					coreData={coreData}
					extensionData={memoedExtensionData}
					extensionPayload={extensionPayload}
					editorAPI={editorAPI}
					extensionViewData={extensionViewData}
					locale={locale}
					flags={flags}
					getContextToken={getContextToken}
					productExtensionHandlers={productExtensionHandlers}
					customEditContext={customEditContext}
					isEditing={isEditing}
					onForgeDocUpdated={handleOnMacroUpdated}
					viewportDimensions={viewportDimensions}
					useRouteActions={useRouteActions}
				/>
			</ReservedHeightWrapper>
		);
	};

	return (
		<div
			data-testid="ForgeExtensionContainer"
			style={{
				background: isEditing ? token('elevation.surface') : 'transparent',
				padding: isEditing && !isInline ? APP_CONTAINER_PADDING : 0,
				display: isInline ? 'inline-block' : 'block',
				verticalAlign: isInline ? 'middle' : '',
			}}
			css={styles1}
			ref={elRef}
		>
			{renderRenderer()}
			{isEditing && !hideGlassPane && <div data-testid="GlassPane" css={styles2} />}
		</div>
	);
};

const ForgeUIExtensionWrapperWithBoundary = (props: ForgeUIExtensionWrapperProps): JSX.Element => {
	const { extension, product, environment, page, analyticsWebClient, customEditContext } = props;

	const children = (
		<ForgeErrorBoundary>
			<ForgeUIExtensionWrapper {...props} />
		</ForgeErrorBoundary>
	);

	return (
		<UFOThirdPartySegment name="forge-ui-app-content-root">
			{/* This Suspense fallback is for the ForgeErrorBoundary */}
			<Suspense fallback={null}>
				<ForgeUIExtensionPointProvider
					analyticsWebClient={analyticsWebClient}
					product={product}
					environment={environment}
					page={page}
					localId={extension.parameters?.localId}
					isCustomConfig={!!customEditContext}
				>
					{extension.parameters ? (
						<ForgeUIExtensionAnalyticsContext
							localId={extension.parameters.localId}
							extensionId={extension.parameters.extensionId}
						>
							{children}
						</ForgeUIExtensionAnalyticsContext>
					) : (
						children
					)}
				</ForgeUIExtensionPointProvider>
			</Suspense>
		</UFOThirdPartySegment>
	);
};

export default ForgeUIExtensionWrapperWithBoundary;
